import React, { useEffect, useRef, useState } from "react";
import { Box, View } from "native-base";
import { LayoutChangeEvent, Platform } from "react-native";
import { useAppContext } from "../../../Universal/API/Context-API/AppContext";
import PressableWrapp from "../../../Universal/Buttons/PressableReact/PressableWrapp";
import { copyArt } from "../utils";
import { UNIT_LIST_V3_WEB_ROOT_MARGIN } from "./constants";
import {
  getCollectionLabel,
  getUnitCardMediaMetrics,
  normalizeText,
} from "./helpers";
import UnitCardBody from "./Components/UnitCardBody";
import UnitCardMedia from "./Components/UnitCardMedia";
import { UnitCardV3Props } from "./types";

const UnitCardV3: React.FC<UnitCardV3Props> = ({
  item,
  onPress,
  onPressCollection,
  shouldLoadMedia = true,
  styles,
}) => {
  const { isSmallVersion, isMediumVersion, scaleAll } = useAppContext();
  const [isHovered, setIsHovered] = useState(false);
  const [mediaEnabled, setMediaEnabled] = useState(shouldLoadMedia);
  const [cardWidth, setCardWidth] = useState(0);
  const pressableRef = useRef<any>(null);

  useEffect(() => {
    if (shouldLoadMedia) {
      setMediaEnabled(true);
    }
  }, [shouldLoadMedia]);

  useEffect(() => {
    if (mediaEnabled) return;
    if (Platform.OS !== "web") return;

    const node = pressableRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          setMediaEnabled(true);
          observer.disconnect();
          break;
        }
      },
      {
        root: null,
        rootMargin: UNIT_LIST_V3_WEB_ROOT_MARGIN,
        threshold: 0.01,
      }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [mediaEnabled]);

  useEffect(() => {
    const node = pressableRef.current;
    if (!node || typeof node.addEventListener !== "function") return;

    const handler = (e: MouseEvent) => {
      if (e.button !== 1) return;
      e.preventDefault();
      onPress();
    };

    node.addEventListener("auxclick", handler);
    node.addEventListener("mousedown", handler);

    return () => {
      node.removeEventListener("auxclick", handler);
      node.removeEventListener("mousedown", handler);
    };
  }, [onPress]);

  const collectionLabel = getCollectionLabel(item.unit);
  const artLabel = normalizeText(item.unit.art);
  const { mediaHeight, carouselWidth, imageMaxWidth } = React.useMemo(
    () =>
      getUnitCardMediaMetrics({
        isSmallVersion,
        isMediumVersion,
        scaleAll,
      }),
    [isMediumVersion, isSmallVersion, scaleAll]
  );

  const handleCopyArt = React.useCallback((event?: any) => {
    event?.stopPropagation?.();
    if (!artLabel) return;
    copyArt({ art: artLabel });
  }, [artLabel]);

  const handleOpenCollection = React.useCallback((event?: any) => {
    event?.stopPropagation?.();
    if (!collectionLabel || !onPressCollection) return;
    onPressCollection(collectionLabel);
  }, [collectionLabel, onPressCollection]);

  const handleCardLayout = React.useCallback((event: LayoutChangeEvent) => {
    const nextWidth = Math.round(event.nativeEvent.layout.width);
    if (nextWidth > 0) {
      setCardWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    }
  }, []);

  return (
    <Box style={styles.cardOuter}>
      <PressableWrapp
        ref={pressableRef}
        style={styles.cardPressable}
        onPress={onPress}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
      >
        <View
          style={[styles.card, isHovered ? styles.cardHovered : null]}
          onLayout={handleCardLayout}
        >
          <UnitCardMedia
            item={item}
            artLabel={artLabel}
            collectionLabel={collectionLabel}
            mediaEnabled={mediaEnabled}
            mediaHeight={mediaHeight}
            carouselWidth={carouselWidth}
            imageMaxWidth={imageMaxWidth}
            styles={styles}
            onCopyArt={handleCopyArt}
          />
          <UnitCardBody
            item={item}
            isSmallVersion={isSmallVersion}
            cardWidth={cardWidth}
            styles={styles}
          />
        </View>
      </PressableWrapp>
    </Box>
  );
};

export default React.memo(UnitCardV3);
